/* * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights
Reserved. * * Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with * the License. A copy of the
License is located at * * http://aws.amazon.com/apache2.0/ * * or in the
"license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR * CONDITIONS OF ANY KIND, either express or
implied. See the License for the specific language governing permissions * and
limitations under the License. */

<template>
  <ValidationObserver ref="obs" v-slot="{ invalid, validated }">
    <v-card
      max-width="350"
      min-width="350"
      color="transparent"
      :elevation="0"
      v-bind:data-test="auth.forgotPassword.section"
    >
      <v-card-title v-bind:data-test="auth.forgotPassword.headerSection">
        {{ options.header }}
      </v-card-title>
      <v-alert
        v-if="alert.message"
        v-model="alert.show"
        prominent
        dense
        dismissible
        :type="alert.type || 'error'"
        transition="scale-transition"
      >
        <v-layout align-center wrap>
          <div>
            {{ alert.message }}
          </div>
        </v-layout>
      </v-alert>
      <v-card-text v-bind:data-test="auth.forgotPassword.bodySection">
        <form>
          <amplify-username-field
            v-bind:usernameAttributes="usernameAttributes"
            v-on:username-field-changed="usernameFieldChanged"
          >
          </amplify-username-field>

          <ValidationProvider
            v-if="sent"
            name="code"
            rules="required"
            v-slot="{ errors, valid }"
          >
            <v-text-field
              v-model="code"
              :placeholder="$Amplify.I18n.get('Code')"
              :label="`${$Amplify.I18n.get('Code')} *`"
              autofocus
              v-bind:data-test="auth.forgotPassword.codeInput"
              :error-messages="errors"
              :success="valid"
            ></v-text-field>
          </ValidationProvider>

          <ValidationProvider
            v-if="sent"
            name="password"
            rules="required"
            v-slot="{ errors, valid }"
          >
            <v-text-field
              autofocus
              v-model="password"
              :placeholder="$Amplify.I18n.get('Enter your new password')"
              :label="`${$Amplify.I18n.get('New Password')} *`"
              :append-icon="passwordVisible ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="passwordVisible = !passwordVisible"
              :type="passwordVisible ? 'text' : 'password'"
              v-on:keyup.enter="submit"
              v-bind:data-test="auth.forgotPassword.newPasswordInput"
              :error-messages="errors"
              :success="valid"
              data-vv-name="password"
            ></v-text-field>
          </ValidationProvider>

          <div>
            <a
              v-if="!sent"
              v-on:click="signIn"
              v-bind:data-test="auth.forgotPassword.backToSignInLink"
              >{{ $Amplify.I18n.get("Back to Sign In") }}</a
            >
            <a
              v-if="sent"
              v-on:click="submit"
              v-bind:data-test="auth.forgotPassword.resentCodeLink"
              >{{ $Amplify.I18n.get("Resend Code") }}</a
            >
          </div>
        </form>
      </v-card-text>
      <v-card-actions v-bind:data-test="auth.signIn.footerSection">
        <v-container fluid ma-0 pa-0>
          <v-layout wrap>
            <v-flex pa-2 order-xl1 order-lg1 order-md1 order-sm1 order-xs2>
              <!--  <v-btn
                block
                v-if="options.isSignUpDisplayed"
                v-on:click="signUp"
                outlined
                color="primary"
                v-bind:data-test="auth.signIn.createAccountLink"
                >{{ $Amplify.I18n.get("Create account") }}</v-btn
              > -->
            </v-flex>

            <v-flex pa-2 order-xl2 order-lg2 order-md2 order-sm2 order-xs1>
              <v-btn
                v-if="!sent"
                block
                v-on:click="submit"
                v-bind:data-test="auth.forgotPassword.sendCodeButton"
                color="primary"
                :disabled="
                  invalid || !validated || loadingBtn || !forgotPwUsername
                "
                :loading="loadingBtn"
              >
                {{ $Amplify.I18n.get("Send Code") }}
                <template v-slot:loader>
                  <span>{{ $Amplify.I18n.get("Wait...") }}</span>
                </template>
              </v-btn>

              <v-btn
                v-if="sent"
                block
                v-on:click="verify"
                v-bind:data-test="auth.forgotPassword.submitButton"
                color="primary"
                :disabled="
                  invalid || !validated || loadingBtn || !forgotPwUsername
                "
                :loading="loadingBtn"
              >
                {{ $Amplify.I18n.get("Submit") }}
                <template v-slot:loader>
                  <span>{{ $Amplify.I18n.get("Wait...") }}</span>
                </template>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-actions>
    </v-card>
  </ValidationObserver>
</template>

<script>
import { AmplifyEventBus } from "aws-amplify-vue";
import * as AmplifyUI from "@aws-amplify/ui";
import Vue from "vue";
import UsernameField from "./UsernameField";
import { auth } from "aws-amplify-vue/src/assets/data-test-attributes";
import { ValidationObserver, ValidationProvider } from "vee-validate";

Vue.component("amplify-username-field", UsernameField);

export default {
  name: "ForgotPassword",
  components: {
    ValidationProvider,
    ValidationObserver
  },
  props: ["forgotPasswordConfig", "usernameAttributes"],
  data() {
    return {
      code: "",
      password: "",
      passwordVisible: false,
      error: "",
      sent: false,
      logger: {},
      amplifyUI: AmplifyUI,
      forgotPwUsername: "",
      auth,
      alert: {
        show: false,
        type: "",
        message: ""
      },
      loadingBtn: false
    };
  },
  computed: {
    options() {
      const defaults = {
        header: this.$Amplify.I18n.get("Reset your password")
      };
      return Object.assign(defaults, this.forgotPasswordConfig || {});
    }
  },
  mounted() {
    this.logger = new this.$Amplify.Logger(this.$options.name);
  },
  methods: {
    submit: function() {
      this.resetAlert();
      this.loadingBtn = true;
      this.$Amplify.Auth.forgotPassword(this.forgotPwUsername)
        .then(() => {
          this.loadingBtn = false;
          this.setAlert({
            message: this.$Amplify.I18n.get("Check your email and copy code"),
            type: "success"
          });
          this.sent = true;
          this.logger.info("forgotPassword success");
        })
        .catch(e => this.setError(e));
    },
    verify: function() {
      this.resetAlert();
      this.loadingBtn = true;
      this.$Amplify.Auth.forgotPasswordSubmit(
        this.forgotPwUsername,
        this.code,
        this.password
      )
        .then(() => {
          this.loadingBtn = false;
          this.logger.info("forgotPasswordSubmit success");
          AmplifyEventBus.$emit("authState", "signIn");
        })
        .catch(e => this.setError(e));
    },
    signIn: function() {
      AmplifyEventBus.$emit("authState", "signIn");
    },
    setError: function(e) {
      this.loadingBtn = false;
      this.error = this.$Amplify.I18n.get(e.message || e);
      this.setAlert({ message: this.error });
      this.logger.error(this.error);
    },
    setAlert: function({ message, type = "error", show = true }) {
      this.alert = {
        message,
        type,
        show
      };
    },
    resetAlert: function() {
      this.setAlert({
        message: "",
        show: false
      });
    },
    usernameFieldChanged: function(data) {
      const { usernameField, username, email, phoneNumber } = data;
      switch (usernameField) {
        case "username":
          this.forgotPwUsername = username;
          break;
        case "email":
          this.forgotPwUsername = email;
          break;
        case "phone_number":
          this.forgotPwUsername = phoneNumber;
          break;
        default:
          break;
      }
    }
  }
};
</script>
